function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import PubSub from "pubsub-js";
import endpoints from "../../endpoints";
import account from "../../data/account";
import { AuthBase } from "../../../plunk/service/auth-base";
import { cleanTitle, titleCase, rangeLabelFor, RANGES } from "../sales/territory-common";
import { rangeToDates } from "../../functions/range-window";

/*
 * Shared helpers for the Product Explorer's Products + Series tabs
 * (styles: sass/v2/_products-explorer.scss, class prefix px-).
 */

// ── Numbers: whole dollars or compact ($111.3M), never cents ──
const n0 = v => Number(v) || 0;
export function count(v) {
  return Math.round(n0(v)).toLocaleString("en-US");
}
export function dollars(v) {
  const n = n0(v);
  if (!n) return "$0";
  return (n < 0 ? "-$" : "$") + Math.round(Math.abs(n)).toLocaleString("en-US");
}
function compactNum(a) {
  if (a >= 1e9) return (a / 1e9).toFixed(a >= 1e11 ? 0 : a >= 1e10 ? 1 : 2).replace(/\.0+$/, "") + "B";
  if (a >= 1e6) return (a / 1e6).toFixed(a >= 1e8 ? 1 : 2) + "M";
  if (a >= 1e3) return (a / 1e3).toFixed(a >= 1e5 ? 0 : a >= 1e4 ? 1 : 2).replace(/\.0+$/, "") + "K";
  return String(Math.round(a));
}
/** $111.3M / $2.28M / $790K / $812 */
export function money(v) {
  const n = n0(v);
  if (!n) return "$0";
  return (n < 0 ? "-$" : "$") + compactNum(Math.abs(n));
}
/** 1.39M / 48.2K / 812 */
export function countShort(v) {
  const n = n0(v);
  return Math.abs(n) < 10000 ? count(n) : (n < 0 ? "-" : "") + compactNum(Math.abs(n));
}

/** Percent change chip text + direction, or null when there's no usable prior. */
export function pctChange(cur, prev) {
  const c = n0(cur),
    p = n0(prev);
  if (p <= 0) return null;
  const pct = (c - p) / p * 100;
  const r = Math.round(pct);
  return {
    pct,
    dir: r > 0 ? "up" : r < 0 ? "down" : "flat",
    text: `${r > 0 ? "+" : r < 0 ? "−" : ""}${Math.abs(r)}%`
  };
}
export const Change = ({
  c
}) => c ? /*#__PURE__*/React.createElement("span", {
  className: `px-chg ${c.dir}`
}, c.text) : null;

// ── Titles + categories ──
/** LEE-style ALL-CAPS titles get title-cased for display; mixed case stays as written. */
export function displayTitle(t) {
  const s = cleanTitle(t);
  if (!s) return "";
  return /[A-Z]/.test(s) && s === s.toUpperCase() ? titleCase(s) : s;
}
export function catDecode(raw) {
  return String(raw == null ? "" : raw).replace(/&gt;/g, ">").replace(/&lt;/g, "<").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&");
}
/** Category leaf for display ("Furniture > Chairs & Ottomans" → "Chairs & Ottomans"). */
export function catLabel(raw) {
  const decoded = catDecode(raw);
  const leaf = decoded.split(">").pop().trim();
  const s = leaf || decoded;
  return s && s === s.toUpperCase() ? titleCase(s) : s;
}

// ── Range label with dates: "This year, Jan 1 to Oct 9, 2026" ──
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const md = d => `${MON[d.getMonth()]} ${d.getDate()}`;
export function rangeWithDates(range) {
  const base = rangeLabelFor(range, RANGES);
  const pair = rangeToDates(range);
  if (!pair || /^custom_/.test(String(range))) return base;
  const label = base.charAt(0) + base.slice(1).toLowerCase();
  const [a, b] = pair;
  let dates;
  if (a.getTime() === b.getTime()) dates = `${md(a)}, ${a.getFullYear()}`;else if (a.getFullYear() === b.getFullYear()) dates = `${md(a)} to ${md(b)}, ${b.getFullYear()}`;else dates = `${md(a)}, ${a.getFullYear()} to ${md(b)}, ${b.getFullYear()}`;
  return `${label}, ${dates}`;
}

// ── Thumbnail with the furniture-icon fallback ──
export class Thumb extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      broken: false
    });
  }
  componentDidUpdate(prev) {
    if (prev.src !== this.props.src && this.state.broken) this.setState({
      broken: false
    });
  }
  render() {
    const {
      src
    } = this.props;
    if (!src || this.state.broken) return /*#__PURE__*/React.createElement("span", {
      className: "px-thumb empty",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("i", {
      className: "material-icons"
    }, "weekend"));
    return /*#__PURE__*/React.createElement("span", {
      className: "px-thumb"
    }, /*#__PURE__*/React.createElement("img", {
      src: src,
      alt: "",
      loading: "lazy",
      onError: () => this.setState({
        broken: true
      })
    }));
  }
}

// ── Popover menu (the Reports list "···" pattern) ──
export class Menu extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      open: false,
      pos: null
    });
    _defineProperty(this, "onDoc", e => {
      if (this._wrap && !this._wrap.contains(e.target)) this.close();
    });
    _defineProperty(this, "onKeyDoc", e => {
      if (e.key === "Escape") this.close();
    });
    _defineProperty(this, "close", () => {
      if (this.state.open) {
        this.unbind();
        this.setState({
          open: false
        });
      }
    });
    _defineProperty(this, "toggle", e => {
      e.stopPropagation();
      e.preventDefault();
      if (this.state.open) {
        this.close();
        return;
      }
      const r = e.currentTarget.getBoundingClientRect();
      const below = window.innerHeight - r.bottom > 260;
      const alignLeft = this.props.align === "left";
      const h = alignLeft ? {
        left: r.left
      } : {
        right: window.innerWidth - r.right
      };
      this.setState({
        open: true,
        pos: below ? {
          top: r.bottom + 6,
          ...h
        } : {
          bottom: window.innerHeight - r.top + 6,
          ...h
        }
      });
      this.bind();
    });
  }
  componentWillUnmount() {
    this.unbind();
  }
  bind() {
    document.addEventListener("mousedown", this.onDoc);
    document.addEventListener("keydown", this.onKeyDoc);
    window.addEventListener("scroll", this.close, true);
    window.addEventListener("resize", this.close);
  }
  unbind() {
    document.removeEventListener("mousedown", this.onDoc);
    document.removeEventListener("keydown", this.onKeyDoc);
    window.removeEventListener("scroll", this.close, true);
    window.removeEventListener("resize", this.close);
  }
  render() {
    const {
      label,
      items,
      trigger,
      triggerCls
    } = this.props;
    const {
      open,
      pos
    } = this.state;
    const list = (items || []).filter(Boolean);
    if (!list.length) return null;
    return /*#__PURE__*/React.createElement("span", {
      className: "px-menu",
      ref: el => {
        this._wrap = el;
      },
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: triggerCls || "px-menu-btn",
      "aria-label": label,
      "aria-haspopup": "menu",
      "aria-expanded": open,
      onClick: this.toggle
    }, trigger || "···"), open ? /*#__PURE__*/React.createElement("div", {
      className: "px-menu-pop",
      role: "menu",
      style: pos
    }, list.map(it => /*#__PURE__*/React.createElement("button", {
      key: it.key || it.label,
      type: "button",
      role: "menuitem",
      className: it.on ? "on" : "",
      disabled: it.disabled,
      onClick: () => {
        this.close();
        it.onClick && it.onClick();
      }
    }, it.label))) : null);
  }
}

// ── Ask Plunk (the app's assistant panel listens on this topic) ──
export const askPlunk = starter => PubSub.publish("plunk-assistant-toggle", {
  starter
});
function toast(msg) {
  const t = document.querySelector("paper-toast");
  if (t) t.show(msg);
}

/*
 * "Forecast a look-alike": seed a Similarity Forecast from the product's image
 * via the existing multipart contract (copied from product-performance/index.jsx).
 * p = { productId, title, imageLink, category? }. Resolves true when queued.
 */
export async function seedLookAlike(p, history) {
  if (!p || !p.imageLink) {
    toast("This product has no image to start a look-alike forecast from.");
    return false;
  }
  try {
    const img = await fetch(p.imageLink);
    if (!img.ok) throw new Error("image fetch " + img.status);
    const blob = await img.blob();
    const fd = new FormData();
    const ext = (p.imageLink.split("?")[0].split(".").pop() || "jpg").slice(0, 5);
    fd.append("file", blob, `${p.productId}.${ext}`);
    const ascii = s => String(s || "").replace(/[^\x20-\x7E]/g, "").slice(0, 120);
    // X-CATEGORY is required (non-empty) and drives comparable matching:
    // the product's category leaf when known, else the title's trailing type word.
    let category = p.category ? catLabel(p.category) : "";
    if (!category) {
      const baseTitle = String(p.title || "").split("-")[0].replace(/\(.*?\)/g, "").trim();
      const lastWord = baseTitle.split(/\s+/).pop() || "";
      category = /^[A-Za-z]{3,}$/.test(lastWord) ? lastWord : "Furniture";
    }
    const res = await fetch(endpoints.forecast.root + "/forecast/product-similarity-forecast", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + (AuthBase.idToken || ""),
        "X-APP-ID": (account.get() || {}).tenantId || "",
        "X-NAME": ascii(`${p.title || p.productId} - look-alike`),
        "X-DESCRIPTION": ascii(`Seeded from Products (${p.productId})`),
        "X-CATEGORY": ascii(category)
      },
      body: fd
    });
    if (!res.ok) throw new Error("HTTP " + res.status);
    toast("Look-alike forecast started. Results show up in Product forecasts in a few minutes.");
    if (history) history.push("/Forecast/Similarity");
    return true;
  } catch (err) {
    toast("Could not start the look-alike forecast. Try again, or upload the image from Product forecasts.");
    return false;
  }
}